import { getShortMonthName } from '@gitbook/util-format';

const SUPPORTS_DATE_TIME_FORMAT =
    'DateTimeFormat' in Intl && Intl.DateTimeFormat.supportedLocalesOf(['en-US']).includes('en-US');

/**
 * Define the formatters once so it's faster than using date.toLocaleDateString() every time.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
 */
const DATE_TIME_DATE_FORMATTER = SUPPORTS_DATE_TIME_FORMAT
    ? new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
      })
    : undefined;

const DATE_TIME_FORMATTER = SUPPORTS_DATE_TIME_FORMAT
    ? new Intl.DateTimeFormat('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short',
      })
    : undefined;

const DATE_TIME_DAY_FORMATTER = SUPPORTS_DATE_TIME_FORMAT
    ? new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
      })
    : undefined;

/** Returns the month for the given date padded with a 0 if needed. */
export function get2DigitsMonth(date: Date): string {
    return `${date.getMonth() + 1}`.padStart(2, '0');
}

/** Returns the day for the given date padded with a 0 if needed. */
export function get2DigitsDay(date: Date): string {
    return `${date.getDate()}`.padStart(2, '0');
}

const DAYS_MAP = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
};

/**
 * Return the short day name for the given date.
 */
export function getShortWeekdayName(date: Date): string {
    const day = date.getDay();
    return DAYS_MAP[day];
}

/**
 * A function to format the given string date, or Date instance, with the Intl.DateTimeFormat API
 * or using a fallback for non-supported browsers.
 *
 * Used locale and options:
 * - locale: 'en-US'
 * - options: { year: 'numeric', month: 'short', day: '2-digit' }
 *
 * Example output with the predefined defaults:
 * - Jun 08, 2023
 * - Jun 08, 2023, 3:45 PM (with showTime: true)
 */
export function toLocaleDateString(date: Date | string, showTime?: boolean): string {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    if (showTime && DATE_TIME_FORMATTER) {
        return DATE_TIME_FORMATTER.format(date);
    }

    if (DATE_TIME_DATE_FORMATTER) {
        return DATE_TIME_DATE_FORMATTER.format(date);
    }

    // Fallback for non-supported browsers
    const day = get2DigitsDay(date);
    const month = getShortMonthName(date);
    const year = date.getFullYear();

    if (showTime) {
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${month} ${day}, ${year}, ${displayHours}:${minutes} ${ampm}`;
    }

    return `${month} ${day}, ${year}`;
}

/**
 * A function to format the given string date, or Date instance, with the Intl.DateTimeFormat API
 * or using a fallback for non-supported browsers, returning the day of the week and the date
 * without the year.
 *
 * * Used locale and options:
 * - locale: 'en-US'
 * - options: { weekday: 'short', month: 'short', day: 'numeric' }
 *
 * Example output with the predefined defaults:
 * - Sun, Jun 8
 */
export function toDayLocaleDateString(date: Date | string): string {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    if (DATE_TIME_DAY_FORMATTER) {
        return DATE_TIME_DAY_FORMATTER.format(date);
    }

    // Fallback for non-supported browsers
    const weekDay = getShortWeekdayName(date);
    const day = date.getDate();
    const month = getShortMonthName(date);

    return `${weekDay}, ${month} ${day}`;
}

/**
 * A function to format the given string time, or Date instance, with the Intl.DateTimeFormat API
 * or using a fallback for non-supported browsers.
 */
export function toLocaleDateTimeString(date: Date | string): string {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    if (DATE_TIME_FORMATTER) {
        return DATE_TIME_FORMATTER.format(date);
    }

    return date.toISOString();
}
